import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '@/components/ui/Card';
import { playerPortalAPI } from '@/services/api';
import { calendarAPI, CalendarItem } from '@/services/calendarApi';
import { useAuth } from '@/context/AuthContext';
import PlayerEventDetail from '@/components/player/PlayerEventDetail';
import DelayedLoading from '@/components/ui/DelayedLoading';
import LoadingStatus from '@/components/ui/LoadingStatus';

interface DashboardProfile {
  id: string;
  name: string;
  position: string;
  age: number;
  team: string;
  photoUrl: string | null;
  injuryStatus: string;
}

interface DashboardMedical {
  injuryStatus: string;
  injuryType: string | null;
  expectedReturnDate: string | null;
}

interface DashboardData {
  profile: DashboardProfile;
  medical: DashboardMedical;
}

const injuryStatusColor = (status: string) => {
  switch (status) {
    case 'fit': return 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300';
    case 'injured': return 'bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-300';
    case 'recovering': return 'bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300';
    case 'questionable': return 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300';
    default: return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300';
  }
};

const PlayerDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const [data, setData] = useState<DashboardData | null>(null);
  const [upcoming, setUpcoming] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [detailItem, setDetailItem] = useState<CalendarItem | null>(null);

  const loadUpcoming = useCallback(async () => {
    const res = await calendarAPI.myFeed();
    const now = Date.now();
    const next = (res.data || [])
      .filter((it) => new Date(it.start).getTime() >= now)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
      .slice(0, 5);
    setUpcoming(next);
  }, []);

  useEffect(() => {
    Promise.all([
      playerPortalAPI.getDashboard().then((res: { data: DashboardData }) => setData(res.data)),
      loadUpcoming(),
    ])
      .catch(() => setError('Failed to load dashboard'))
      .finally(() => setLoading(false));
  }, [loadUpcoming]);

  if (loading) {
    return (
      <DelayedLoading loading>
        <LoadingStatus variant="panel" label="Loading player dashboard" className="py-12" />
      </DelayedLoading>
    );
  }

  if (error || !data) {
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-md text-sm">
        {error || 'No data available'}
      </div>
    );
  }

  const { profile, medical } = data;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        My Dashboard
        {profile.name && <span className="text-gray-500 dark:text-gray-400 font-normal text-lg ml-3">- {profile.name}</span>}
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Profile</h2>
          <div className="flex items-center gap-4">
            {profile.photoUrl ? (
              <img src={profile.photoUrl} alt={profile.name} className="w-16 h-16 rounded-full object-cover" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 text-2xl font-bold">
                {profile.name?.charAt(0) || '?'}
              </div>
            )}
            <div>
              <div className="font-medium text-gray-900 dark:text-gray-100">{profile.name}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{profile.position}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{profile.team}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">Age: {profile.age}</div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Medical</h2>
            <Link to="/player/medical" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">View reports</Link>
          </div>
          <div className="space-y-3">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium capitalize ${injuryStatusColor(medical.injuryStatus)}`}>
              {medical.injuryStatus}
            </span>
            {medical.injuryType && (
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Injury:</span> {medical.injuryType}
              </div>
            )}
            {medical.expectedReturnDate && (
              <div className="text-sm text-gray-700 dark:text-gray-300">
                <span className="font-medium">Expected return:</span> {new Date(medical.expectedReturnDate).toLocaleDateString()}
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Upcoming</h2>
          <Link to="/player/calendar" className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Full calendar</Link>
        </div>
        {upcoming.length > 0 ? (
          <div className="space-y-2">
            {upcoming.map((it) => {
              const start = new Date(it.start);
              const today = start.toDateString() === new Date().toDateString();
              return (
                <button
                  type="button"
                  key={`${it.source}-${it.id}`}
                  onClick={() => setDetailItem(it)}
                  className="flex w-full items-center gap-3 text-left bg-gray-50 dark:bg-gray-800/50 rounded-lg px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  <div
                    className={`flex w-14 shrink-0 flex-col items-center justify-center rounded-lg py-1.5 ${
                      today
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    <span className="text-[10px] font-medium uppercase leading-none">
                      {start.toLocaleDateString(undefined, { weekday: 'short' })}
                    </span>
                    <span className="text-lg font-bold leading-tight">{start.getDate()}</span>
                    <span className="text-[10px] uppercase leading-none">
                      {start.toLocaleDateString(undefined, { month: 'short' })}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{it.title}</div>
                    {it.location && <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{it.location}</div>}
                    <div className="mt-0.5 text-xs tabular-nums text-gray-500 dark:text-gray-400">
                      {start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">Nothing scheduled.</p>
        )}
      </Card>

      {detailItem && (
        <PlayerEventDetail
          item={detailItem}
          currentUserId={user?.id}
          onClose={() => setDetailItem(null)}
          onDeleted={() => {
            setDetailItem(null);
            loadUpcoming();
          }}
        />
      )}
    </div>
  );
};

export default PlayerDashboardPage;
